import React, { useMemo, useRef, useState } from "react";
import { Avatar, AvatarImage, AvatarFallback } from "@primitives/avatar"; // adjust import paths as needed

import { Camera, Trash2 } from "lucide-react";
import { stringToRgbColor } from "@/lib/utils";

import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/primitives/dialog";
import { Button } from "../primitives/button";

type Props = {
  handleFileChange: (e: React.ChangeEvent<HTMLInputElement>) => Promise<void>;
  handleDeleteAvatar: () => void | Promise<void>;
  blobUrl: string;
  isUploading: boolean;
  name?: string;
  /** String used to derive the placeholder background color. Defaults to `name`. */
  colorSeed?: string;
  defaultAvatarUrl?: string;
};

export default function AvatarUploader({
  handleFileChange,
  handleDeleteAvatar,
  blobUrl,
  isUploading,
  name,
  colorSeed,
  defaultAvatarUrl,
}: Props) {
  const avatarColor = useMemo(
    () => stringToRgbColor(colorSeed ?? name ?? ""),
    [colorSeed, name],
  );
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { t } = useTranslation();

  const [openDialog, setOpenDialog] = useState<"delete-avatar" | null>(null);

  // improve the component menu & overall logic (remove avatar vs update empty user "picture" string)
  const _handleDeleteAvatar = async () => {
    try {
      await handleDeleteAvatar();
    } finally {
      setOpenDialog(null);
    }
  };

  return (
    <div className="relative flex flex-col items-center">
      <Avatar
        aria-label={t("aria.avatar")}
        style={{ backgroundColor: `rgba(${avatarColor}, 0.7)` }}
        className="relative size-24 lg:cursor-pointer"
        onClick={() => fileInputRef.current?.click()}
      >
        <AvatarImage src={blobUrl || defaultAvatarUrl} alt="Avatar" />
        <AvatarFallback className="bg-transparent">
          {name && name.length > 1 ? (
            name.slice(0, 2).toUpperCase()
          ) : (
            <div className="my-4 flex size-24 shrink-0 items-center justify-center rounded-full bg-accent-foreground/80 align-middle">
              <Camera strokeWidth={1} className="size-10 text-background" />
            </div>
          )}
        </AvatarFallback>
        {(blobUrl || defaultAvatarUrl) && (
          <div className="absolute left-0 top-0 flex size-full cursor-pointer items-center justify-center">
            <Camera
              className="size-10 rounded-full bg-primary/30 p-1 opacity-70"
              strokeWidth={1}
            />
          </div>
        )}
        {isUploading && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <div className="size-24 animate-spin rounded-full border-2 border-b-background border-l-secondary border-r-primary border-t-transparent shadow-md blur-sm" />
          </div>
        )}
      </Avatar>
      {defaultAvatarUrl && (
        <Trash2
          className="absolute -right-4 bottom-0 size-5 cursor-pointer"
          // onClick={onDeleteAvatar}
          onClick={() => setOpenDialog("delete-avatar")}
        />
      )}

      {/* Hidden file input for image upload/capture */}
      <input
        type="file"
        accept="image/*"
        // "capture" hints mobile browsers to use the camera (can be "user" for the front camera)
        // capture="environment"
        ref={fileInputRef}
        className="hidden"
        onChange={handleFileChange}
      />

      <Dialog
        open={openDialog === "delete-avatar"}
        onOpenChange={() => {
          if (isUploading) {
            return;
          }
          setOpenDialog(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("avatar.title")}</DialogTitle>
            <DialogDescription>{t("avatar.description")}</DialogDescription>
          </DialogHeader>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              disabled={isUploading}
              onClick={() => setOpenDialog(null)}
            >
              {t("cancel")}
            </Button>
            <Button
              variant="destructive"
              disabled={isUploading}
              onClick={() => _handleDeleteAvatar()}
            >
              {isUploading ? t("removing") : t("remove")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
